import React from 'react';

import { toast } from 'material-react-toastify';

import { useContentViewStore } from '@/lib/store/contentViewStore';
import { ExploreBlockType } from '@addmile/backend-client';

import { useContentQueries } from '../services/ContentManagerService';
import CollectionBlocksView from './CollectionBlocksView';
import ContentEditorView from './ContentEditorView';
import ContentListView from './ContentListView';
import CopyBlockModal from './CopyBlockModal/CopyBlockModal';
import CreateCollectionView from './CreateCollectionView';
import EditCollectionView from './EditCollectionView';

export const ContentManager: React.FC = () => {
  const {
    viewMode,
    editingSlug,
    draftType,
    editCollectionSlug,
    collectionBlocksSlug,
    setViewMode,
    setEditingSlug,
    setDraftType,
    setEditCollectionSlug,
    setCollectionBlocksSlug,
    resetView,
    showCopyBlockModal,
    setShowCopyBlockModal,
  } = useContentViewStore();

  const { useDeleteCollection, useDeleteBlock } = useContentQueries();
  const deleteCollectionMutation = useDeleteCollection();
  const deleteBlockMutation = useDeleteBlock();

  const handleCreateBlockByType = (type: ExploreBlockType) => {
    setDraftType(type);
    setEditingSlug(undefined);
    setViewMode('editor');
  };

  const handleCreateCollection = () => {
    setViewMode('createCollection');
  };

  const handleEditBlock = (slug: string) => {
    setEditingSlug(slug);
    setDraftType(undefined);
    setViewMode('editor');
  };

  const handleEditCollection = (slug: string) => {
    setEditCollectionSlug(slug);
    setViewMode('editCollection');
  };

  const handleViewCollectionBlocks = (slug: string) => {
    setCollectionBlocksSlug(slug);
    setViewMode('collectionBlocks');
  };

  const handleBackToList = () => {
    resetView();
  };

  const handleSaveSuccess = () => {
    handleBackToList();
  };

  const handleDeleteCollection = async (slug: string) => {
    try {
      await deleteCollectionMutation.mutateAsync(slug);
      toast.success('Collection deleted successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete collection');
    }
  };

  const handleDeleteBlock = async (slug: string) => {
    try {
      await deleteBlockMutation.mutateAsync(slug);
      toast.success('Block deleted successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete block');
    }
  };

  const handleCopyBlock = async (slug: string) => {
    useContentViewStore.setState({ showCopyBlockModal: true, copyBlockSlug: slug });
  };

  switch (viewMode) {
    case 'list':
      return (
        <>
          <CopyBlockModal
            isPopupOpen={showCopyBlockModal}
            closePopup={() => setShowCopyBlockModal(false)}
          />

          <ContentListView
            onCreateCollection={handleCreateCollection}
            onCreateBlock={handleCreateBlockByType}
            onEditBlock={handleEditBlock}
            onEditCollection={handleEditCollection}
            onViewCollectionBlocks={handleViewCollectionBlocks}
            onDeleteCollection={handleDeleteCollection}
            onDeleteBlock={handleDeleteBlock}
            onCopyBlock={handleCopyBlock}
          />
        </>
      );

    case 'editor':
      return (
        <ContentEditorView
          editingSlug={editingSlug}
          draftType={draftType}
          onBack={handleBackToList}
          onSaveSuccess={handleSaveSuccess}
        />
      );

    case 'createCollection':
      return <CreateCollectionView onBack={handleBackToList} onSaveSuccess={handleSaveSuccess} />;

    case 'editCollection':
      if (!editCollectionSlug) {
        return null;
      }
      return (
        <EditCollectionView
          collectionSlug={editCollectionSlug}
          onBack={handleBackToList}
          onSaveSuccess={handleSaveSuccess}
        />
      );

    case 'collectionBlocks':
      if (!collectionBlocksSlug) {
        return null;
      }
      return (
        <>
          <CopyBlockModal
            isPopupOpen={showCopyBlockModal}
            closePopup={() => setShowCopyBlockModal(false)}
          />

          <CollectionBlocksView
            collectionSlug={collectionBlocksSlug}
            onBack={handleBackToList}
            onDeleteBlock={handleDeleteBlock}
            onCopyBlock={handleCopyBlock}
          />
        </>
      );

    default:
      return null;
  }
};

export default ContentManager;
